import { Fragment, PureComponent, ReactNode } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { RouterProps } from '../../../../../pages/prescriptionPad/magicBox/routerProps';
import { ThunkDispatch } from 'redux-thunk';
import { GlobalState } from '../../../../../store/reducers';
import { Actions } from '../../../../../store/actions';
import getTreatmentPlans, {
    TREATMENT_PLAN_TYPE,
    TreatmentPlan,
} from '../../../../../api/getTreatmentPlans';
import { DentalProcedure, Doctor, Prescription } from '../../../../../store/types/prescription';
import { connect } from 'react-redux';
import {
    getDentalProcedures,
    getOngoingTreatmentPlans,
    getPrescription,
    getRxDoctor,
} from '../../../../../store/selectors/prescriptionPad';
import { captureError, captureErrorString } from '../../../../../pages/captureError';
import { track } from '../../../../../tracking';
import { notification } from 'antd';
import createTreatmentPlan from '../../../../../api/createTreatmentPlan';
import { GO_TO_PRESCRIPTION_PAD } from '../../../../../routes/paths';
import { WithBEPatient } from '../../../../../store/actions/app';
import fetchBookSlotHash, { Res } from '../../../../../api/fetchBookSlotHash';
import { getSelectedClinicId } from '../../../../../store/idbHandler';
import { toggleIframeActionDialog } from '../../../../../store/actions/session';
import BottomActionStrip from './BottomActionStrip';
import OngoingPlan from './OngoingPlan';
import EstimateAndCompletedPlan from './EstimateAndCompletedPlan';
import { AnimatePresence } from 'framer-motion';
import CreateNewEstimates from './CreateNewEstimates';
import TreatmentBilling from './TreatmentBilling';
import { setOngoingTreatmentPlans, showDialog } from '../../../../../store/actions/prescriptionPad';
import { getPtDetailsStringFromPrescription, getBillingPopupDataFromPrescription } from './utils';
import { AntTooltip } from '../../../../common/AntTooltip';
import { PAD_V2_CONTAINER } from '../PadContainer';

export type TreatmentPlansFetchState = 'loading' | 'success' | 'error';

export type ACTIVE_TAB = TREATMENT_PLAN_TYPE | string;

export const VALID_ACTIVE_TAB_SET: Set<ACTIVE_TAB> = new Set([
    TREATMENT_PLAN_TYPE.ESTIMATE,
    TREATMENT_PLAN_TYPE.COMPLETED_PLAN,
]);

interface Props extends RouteComponentProps<RouterProps> {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    prescription?: Prescription;
    patientId: string;
    ongoingTreatmentPlans: TreatmentPlan[];
    dentalProcedures?: DentalProcedure[];
    docProfile?: Doctor;
}

interface State {
    estimatesFetchState?: TreatmentPlansFetchState;
    estimates?: TreatmentPlan[];
    completedPlansFetchState?: TreatmentPlansFetchState;
    completedPlans?: TreatmentPlan[];
    activeTab: ACTIVE_TAB;
    visibleItems?: Set<string>;
    isCreateNewEstimatesModalVisible?: boolean;
    treatmentBilling?: TreatmentPlan;
    editEstimate?: TreatmentPlan;
    isMarkingAsCompleted?: boolean;
    isForWorkDone?: boolean;
}

class PPTreatmentPlan extends PureComponent<Props, State> {
    state: State = {
        activeTab: TREATMENT_PLAN_TYPE.ESTIMATE,
    };

    componentDidMount(): void {
        const { patientId, ongoingTreatmentPlans } = this.props;
        const { fetchEstimates } = this;
        if (ongoingTreatmentPlans.length > 0) {
            this.setState({
                activeTab: ongoingTreatmentPlans[0].plan_id,
            });
            return;
        }
        patientId && fetchEstimates(patientId);
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        const { patientId: prevPatientId } = prevProps;
        const { patientId: curPatientId } = this.props;
        const { fetchEstimates } = this;
        if (prevPatientId !== curPatientId) fetchEstimates(curPatientId);
    }

    fetchEstimates = async (patientId: string) => {
        this.setState({
            estimatesFetchState: 'loading',
            estimates: [],
        });

        try {
            const { success, treatmentPlans, error = '' } = await getTreatmentPlans(
                patientId,
                TREATMENT_PLAN_TYPE.ESTIMATE,
            );
            if (success) {
                this.setState({
                    estimatesFetchState: 'success',
                    estimates: treatmentPlans,
                });
            } else {
                this.setState({
                    estimatesFetchState: 'error',
                });
                captureErrorString(error);
                track('ERROR', {
                    on: 'getTreatmentPlans',
                    error: error,
                });
                notification.error({
                    message: 'Error in fetching estimates',
                    duration: 5,
                });
            }
        } catch (error) {
            this.setState({
                estimatesFetchState: 'error',
            });
            captureError(error as Error);
            track('ERROR', {
                on: 'getTreatmentPlans',
                error: (error as Error).message,
            });
            notification.error({
                message: 'Error in fetching estimates',
                duration: 5,
            });
        }
        track('FETCH_ESTIMATES_CLICK', { patientId });
    };

    fetchCompletedPlans = async (patientId: string) => {
        this.setState({
            completedPlansFetchState: 'loading',
            completedPlans: [],
        });

        try {
            const { success, treatmentPlans, error = '' } = await getTreatmentPlans(
                patientId,
                TREATMENT_PLAN_TYPE.COMPLETED_PLAN,
            );
            if (success) {
                this.setState({
                    completedPlansFetchState: 'success',
                    completedPlans: treatmentPlans,
                });
            } else {
                this.setState({
                    completedPlansFetchState: 'error',
                });
                captureErrorString(error);
                track('ERROR', {
                    on: 'getTreatmentPlans',
                    error: error,
                });
                notification.error({
                    message: 'Error in fetching completed plans',
                    duration: 5,
                });
            }
        } catch (error) {
            this.setState({
                completedPlansFetchState: 'error',
            });
            captureError(error as Error);
            track('ERROR', {
                on: 'getTreatmentPlans',
                error: (error as Error).message,
            });
            notification.error({
                message: 'Error in fetching completed plans',
                duration: 5,
            });
        }
        track('FETCH_COMPLETED_PLANS_CLICK', { patientId });
    };

    setActiveTab = (type: ACTIVE_TAB) => {
        const { patientId } = this.props;
        const { activeTab } = this.state;
        const { fetchEstimates, fetchCompletedPlans } = this;
        if (activeTab === type) return;
        this.setState({
            activeTab: type,
        });
        if (type === TREATMENT_PLAN_TYPE.ESTIMATE) {
            fetchEstimates(patientId);
        } else if (type === TREATMENT_PLAN_TYPE.COMPLETED_PLAN) {
            fetchCompletedPlans(patientId);
        }
        track('SET_ACTIVE_TAB', { from: 'PP Treatment Plan', activeTab });
    };

    onVisibleItemsChange = (key?: string) => {
        let { visibleItems } = this.state;
        if (!key) return;
        if (visibleItems && visibleItems.has(key)) {
            visibleItems.delete(key);
        } else {
            if (visibleItems) {
                visibleItems.add(key);
            } else {
                visibleItems = new Set([key]);
            }
        }
        this.setState({
            visibleItems: new Set(visibleItems),
        });
        track('VISIBLE_ITEMS_CHANGE', { from: 'PP Treatment Plan' });
    };

    onActivate = async (treatmentPlan: TreatmentPlan) => {
        const { prescription, dispatch, ongoingTreatmentPlans } = this.props;
        if (!prescription) return;
        if (ongoingTreatmentPlans.length > 0) {
            notification.error({
                message:
                    'Cannot activate a treatment plan while there is an ongoing treatment plan',
                duration: 5,
            });
            return;
        }
        const { visitId, id } = prescription;
        const newTreatmentPlan = {
            ...treatmentPlan,
            type: TREATMENT_PLAN_TYPE.ONGOING_PLAN,
            aid: visitId,
            pxid: id,
            updated_at: new Date().toISOString(),
        };
        try {
            const { success, error = '' } = await createTreatmentPlan(newTreatmentPlan);
            if (success) {
                ongoingTreatmentPlans.unshift(newTreatmentPlan);
                dispatch(setOngoingTreatmentPlans(Array.from(ongoingTreatmentPlans)));
                this.setState({
                    activeTab: treatmentPlan.plan_id,
                });
            } else {
                captureErrorString(error);
                track('ERROR', {
                    on: 'createTreatmentPlan',
                    error: error,
                });
                notification.error({
                    message: 'Error in saving treatment plan',
                    duration: 5,
                });
            }
        } catch (err) {
            captureError(err as Error);
            notification.error({
                message: 'Error in saving treatment plan',
                duration: 5,
            });
        }
        track('ON_ACTIVATE_TREATMENT_PLAN', { treatmentPlanId: treatmentPlan.plan_id });
    };

    onMarkAsCompleted = async (treatmentPlan: TreatmentPlan) => {
        const { prescription, dispatch, patientId } = this.props;
        let { ongoingTreatmentPlans } = this.props;
        const { fetchCompletedPlans } = this;
        if (!prescription) return;
        const { visitId, id } = prescription;
        const newTreatmentPlan = {
            ...treatmentPlan,
            type: TREATMENT_PLAN_TYPE.COMPLETED_PLAN,
            aid: visitId,
            pxid: id,
            updated_at: new Date().toISOString(),
        };
        this.setState({
            isMarkingAsCompleted: true,
        });
        try {
            const { success, error = '' } = await createTreatmentPlan(newTreatmentPlan);
            if (success) {
                ongoingTreatmentPlans = ongoingTreatmentPlans.filter(
                    (i) => i.plan_id !== treatmentPlan.plan_id,
                );
                dispatch(setOngoingTreatmentPlans(Array.from(ongoingTreatmentPlans)));
                this.setState({
                    activeTab: TREATMENT_PLAN_TYPE.COMPLETED_PLAN,
                });
                fetchCompletedPlans(patientId);
            } else {
                captureErrorString(error);
                track('ERROR', {
                    on: 'createTreatmentPlan',
                    error: error,
                });
                notification.error({
                    message: 'Error in saving treatment plans',
                    duration: 5,
                });
            }
        } catch (err) {
            captureError(err as Error);
            notification.error({
                message: 'Error in saving treatment plan',
                duration: 5,
            });
        } finally {
            this.setState({
                isMarkingAsCompleted: false,
            });
        }
        track('ON_MARK_AS_COMPLETED_TREATMENT_PLAN', { treatmentPlanId: treatmentPlan.plan_id });
    };

    createNewEstimatesModalToggleHandler = () => {
        const { isCreateNewEstimatesModalVisible } = this.state;
        this.setState({
            isCreateNewEstimatesModalVisible: !isCreateNewEstimatesModalVisible,
            editEstimate: undefined,
        });
        track('CREATE_NEW_ESTIMATES_MODAL_TOGGLE_HANDLER', {
            from: 'PP Treatment Plan',
            action: isCreateNewEstimatesModalVisible ? 'close' : 'open',
        });
    };

    treatmentBillingModalToggleHandler = (
        treatmentPlan?: TreatmentPlan,
        isForWorkDone?: boolean,
    ) => {
        this.setState({
            treatmentBilling: treatmentPlan,
            isForWorkDone,
        });
        track('TREATMENT_BILLING_MODAL_TOGGLE_HANDLER', {
            from: 'PP Treatment Plan',
            treatmentPlanId: treatmentPlan?.plan_id || '',
        });
    };

    onEdit = (estimate: TreatmentPlan) => {
        this.setState({
            editEstimate: estimate,
            isCreateNewEstimatesModalVisible: true,
        });
        track('ON_EDIT_TREATMENT_PLAN', {
            from: 'PP Treatment Plan',
            estimateId: estimate.plan_id,
        });
    };

    onContinue = (): void => {
        const { history, match } = this.props;
        const { params } = match;
        if (!params.pxId) {
            history.replace('/');
        }
        history.push(GO_TO_PRESCRIPTION_PAD(params.pxId, 'edit'));
        track('ON_CONTINUE_TO_RX');
    };

    onBookAppointment = () => {
        const { dispatch, prescription } = this.props;
        if (!prescription) return;
        const { patient } = prescription;
        const { localId, id } = patient;
        const pt = {
            id: localId || id || '',
        };
        WithBEPatient<Res>(pt as any, (bePt) =>
            fetchBookSlotHash({
                id: bePt.id,
            }),
        )(dispatch)
            .then((res) => {
                if (res?.status === 'success' && res.url) {
                    const urlToOpen = new URL(res.url);
                    urlToOpen.searchParams.set('cid', getSelectedClinicId() || '');
                    urlToOpen.searchParams.set('autoOpenSlots', 'true');
                    dispatch(toggleIframeActionDialog(urlToOpen.toString()));
                } else {
                    alert('Failed to book slot');
                }
            })
            .catch(() => {
                alert('Failed to book slot');
            });
        track('ON_BOOK_APPOINTMENT', {
            from: 'PP Treatment Plan',
        });
    };

    onPrint = () => {
        const { dispatch } = this.props;
        dispatch(showDialog('summary'));
        track('ON_PREVIEW_WORK_DONE');
    };

    render(): ReactNode {
        const {
            ongoingTreatmentPlans,
            patientId,
            prescription,
            dentalProcedures,
            docProfile,
        } = this.props;
        const {
            activeTab,
            estimates,
            estimatesFetchState,
            completedPlans,
            completedPlansFetchState,
            visibleItems,
            isCreateNewEstimatesModalVisible,
            treatmentBilling,
            editEstimate,
            isMarkingAsCompleted,
            isForWorkDone,
        } = this.state;
        const {
            onContinue,
            setActiveTab,
            treatmentBillingModalToggleHandler,
            onBookAppointment,
            createNewEstimatesModalToggleHandler,
            onVisibleItemsChange,
            onEdit,
            fetchEstimates,
            onActivate,
            onMarkAsCompleted,
            onPrint,
        } = this;

        const ongoingPlansObject: { [k: string]: TreatmentPlan } = {};
        ongoingTreatmentPlans.forEach((i) => {
            ongoingPlansObject[i.plan_id] = i;
        });
        const ptDetails = getPtDetailsStringFromPrescription(prescription);
        const billingPopupData = getBillingPopupDataFromPrescription(prescription);

        return (
            <div className="col-span-11 overflow-hidden h-full flex flex-col" id={PAD_V2_CONTAINER}>
                <div
                    className="p-16 space-y-10 overflow-auto h-full flex flex-col row-container relative z-0"
                    style={{ paddingBottom: '86px' }}
                >
                    <div
                        className="border flex items-center justify-between space-x-16 rounded-8 bg-darwin-neutrals-50 overflow-hidden"
                        style={{
                            boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                            borderColor: '#E8E8E8',
                            borderBottomWidth: '0px',
                        }}
                    >
                        <div className="flex-1 whitespace-nowrap overflow-auto flex items-center">
                            <button
                                className={`flex items-center space-x-8 px-16 py-12 border-b-2 ${
                                    activeTab === TREATMENT_PLAN_TYPE.ESTIMATE
                                        ? 'border-darwin-accent-bright-blue-800'
                                        : 'border-darwin-neutrals-50'
                                }`}
                                onClick={() => setActiveTab(TREATMENT_PLAN_TYPE.ESTIMATE)}
                            >
                                <p>
                                    <svg
                                        className={`w-9 h-13 ${
                                            activeTab === TREATMENT_PLAN_TYPE.ESTIMATE
                                                ? 'fill-darwin-accent-bright-blue-800'
                                                : 'fill-darwin-neutral_new-800'
                                        }`}
                                        viewBox="0 0 9 13"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M4.5 0.5C5.46094 0.5 6.30469 1.13281 6.60938 2H7.5C8.32031 2 9 2.67969 9 3.5V11C9 11.8438 8.32031 12.5 7.5 12.5H1.5C0.65625 12.5 0 11.8438 0 11V3.5C0 2.67969 0.65625 2 1.5 2H2.36719C2.67188 1.13281 3.51562 0.5 4.5 0.5ZM4.5 2C4.07812 2 3.75 2.35156 3.75 2.75C3.75 3.17188 4.07812 3.5 4.5 3.5C4.89844 3.5 5.25 3.17188 5.25 2.75C5.25 2.35156 4.89844 2 4.5 2ZM2.625 5C2.41406 5 2.25 5.1875 2.25 5.375C2.25 5.58594 2.41406 5.75 2.625 5.75H6.375C6.5625 5.75 6.75 5.58594 6.75 5.375C6.75 5.1875 6.5625 5 6.375 5H2.625Z" />
                                    </svg>
                                </p>
                                <p
                                    className={`text-13 font-700 ${
                                        activeTab === TREATMENT_PLAN_TYPE.ESTIMATE
                                            ? 'text-darwin-accent-bright-blue-800'
                                            : 'text-darwin-neutral_new-800'
                                    }`}
                                >
                                    Plan Estimates
                                </p>
                            </button>
                            {ongoingTreatmentPlans.map((i) => (
                                <button
                                    className={`flex items-center space-x-8 px-16 py-12 border-b-2 ${
                                        activeTab === i.plan_id
                                            ? 'border-darwin-accent-bright-blue-800'
                                            : 'border-darwin-neutrals-50'
                                    }`}
                                    onClick={() => setActiveTab(i.plan_id)}
                                >
                                    <p>
                                        <svg
                                            className={`w-13 h-13 ${
                                                activeTab === i.plan_id
                                                    ? 'fill-darwin-accent-bright-blue-800'
                                                    : 'fill-darwin-neutral_new-800'
                                            }`}
                                            viewBox="0 0 12 13"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M4.3125 2C4.3125 1.17969 4.96875 0.5 5.8125 0.5H6.1875C7.00781 0.5 7.6875 1.17969 7.6875 2V3.59375L9.04688 2.79688C9.75 2.375 10.6875 2.63281 11.0859 3.35938L11.2734 3.66406C11.6953 4.39062 11.4375 5.30469 10.7344 5.72656L9.375 6.5L10.7344 7.29688C11.4375 7.71875 11.6953 8.63281 11.2734 9.33594L11.0859 9.66406C10.6875 10.3906 9.75 10.625 9.04688 10.2266L7.6875 9.42969V11C7.6875 11.8438 7.00781 12.5 6.1875 12.5H5.8125C4.96875 12.5 4.3125 11.8438 4.3125 11V9.42969L2.92969 10.2266C2.22656 10.625 1.3125 10.3906 0.890625 9.66406L0.703125 9.33594C0.28125 8.63281 0.539062 7.71875 1.24219 7.29688L2.625 6.5L1.24219 5.72656C0.539062 5.30469 0.28125 4.39062 0.703125 3.66406L0.890625 3.35938C1.3125 2.63281 2.22656 2.375 2.92969 2.79688L4.3125 3.59375V2ZM5.8125 1.625C5.60156 1.625 5.4375 1.8125 5.4375 2V4.55469C5.4375 4.76562 5.32031 4.95312 5.15625 5.04688C4.96875 5.14062 4.75781 5.14062 4.59375 5.04688L2.36719 3.78125C2.20312 3.66406 1.96875 3.73438 1.85156 3.92188L1.66406 4.25C1.57031 4.41406 1.64062 4.64844 1.80469 4.74219L4.03125 6.03125C4.19531 6.125 4.3125 6.3125 4.3125 6.5C4.3125 6.71094 4.19531 6.89844 4.03125 6.99219L1.80469 8.28125C1.64062 8.375 1.57031 8.60938 1.66406 8.79688L1.85156 9.10156C1.96875 9.28906 2.20312 9.35938 2.36719 9.24219L4.59375 7.97656C4.75781 7.88281 4.96875 7.88281 5.15625 7.97656C5.32031 8.07031 5.4375 8.25781 5.4375 8.46875V11C5.4375 11.2109 5.60156 11.375 5.8125 11.375H6.1875C6.375 11.375 6.5625 11.2109 6.5625 11V8.46875C6.5625 8.25781 6.65625 8.07031 6.84375 7.97656C7.00781 7.85938 7.21875 7.85938 7.40625 7.97656L9.60938 9.24219C9.79688 9.35938 10.0078 9.28906 10.125 9.10156L10.3125 8.77344C10.4062 8.60938 10.3594 8.375 10.1719 8.25781L7.96875 6.99219C7.78125 6.89844 7.6875 6.71094 7.6875 6.5C7.6875 6.3125 7.78125 6.125 7.96875 6.03125L10.1719 4.74219C10.3594 4.64844 10.4062 4.41406 10.3125 4.22656L10.125 3.89844C10.0078 3.73438 9.79688 3.66406 9.60938 3.78125L7.40625 5.04688C7.21875 5.14062 7.00781 5.14062 6.84375 5.04688C6.65625 4.95312 6.5625 4.76562 6.5625 4.55469V2C6.5625 1.8125 6.375 1.625 6.1875 1.625H5.8125Z" />
                                        </svg>
                                    </p>
                                    <p
                                        className={`text-13 font-700 ${
                                            activeTab === i.plan_id
                                                ? 'text-darwin-accent-bright-blue-800'
                                                : 'text-darwin-neutral_new-800'
                                        }`}
                                    >
                                        {i.name}
                                    </p>
                                </button>
                            ))}
                            <button
                                className={`flex items-center space-x-8 px-16 py-12 border-b-2 ${
                                    activeTab === TREATMENT_PLAN_TYPE.COMPLETED_PLAN
                                        ? 'border-darwin-accent-bright-blue-800'
                                        : 'border-darwin-neutrals-50'
                                }`}
                                onClick={() => setActiveTab(TREATMENT_PLAN_TYPE.COMPLETED_PLAN)}
                            >
                                <p>
                                    <svg
                                        className={`w-14 h-14 ${
                                            activeTab === TREATMENT_PLAN_TYPE.COMPLETED_PLAN
                                                ? 'fill-darwin-accent-bright-blue-800'
                                                : 'fill-darwin-neutral_new-800'
                                        }`}
                                        viewBox="0 0 14 13"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M7 1.625C5.24219 1.625 3.64844 2.5625 2.75781 4.0625C1.89062 5.58594 1.89062 7.4375 2.75781 8.9375C3.64844 10.4609 5.24219 11.375 7 11.375C8.73438 11.375 10.3281 10.4609 11.2188 8.9375C12.0859 7.4375 12.0859 5.58594 11.2188 4.0625C10.3281 2.5625 8.73438 1.625 7 1.625ZM7 12.5C4.84375 12.5 2.875 11.375 1.79688 9.5C0.71875 7.64844 0.71875 5.375 1.79688 3.5C2.875 1.64844 4.84375 0.5 7 0.5C9.13281 0.5 11.1016 1.64844 12.1797 3.5C13.2578 5.375 13.2578 7.64844 12.1797 9.5C11.1016 11.375 9.13281 12.5 7 12.5ZM9.64844 5.39844L6.64844 8.39844C6.41406 8.63281 6.0625 8.63281 5.85156 8.39844L4.35156 6.89844C4.11719 6.6875 4.11719 6.33594 4.35156 6.125C4.5625 5.89062 4.91406 5.89062 5.14844 6.125L6.25 7.22656L8.85156 4.625C9.0625 4.39062 9.41406 4.39062 9.64844 4.625C9.85938 4.83594 9.85938 5.1875 9.64844 5.39844Z" />
                                    </svg>
                                </p>
                                <p
                                    className={`text-13 font-700 ${
                                        activeTab === TREATMENT_PLAN_TYPE.COMPLETED_PLAN
                                            ? 'text-darwin-accent-bright-blue-800'
                                            : 'text-darwin-neutral_new-800'
                                    }`}
                                >
                                    Completed Plans
                                </p>
                            </button>
                        </div>
                        <div className="flex items-center space-x-8 pr-16">
                            {!VALID_ACTIVE_TAB_SET.has(activeTab) && (
                                <Fragment>
                                    <AntTooltip
                                        content="Treatment Billing"
                                        placement="bottom"
                                        getPopupContainer={() =>
                                            document.getElementById(
                                                `treatment-billing-${activeTab}`,
                                            ) || document.body
                                        }
                                    >
                                        <button
                                            className="p-6"
                                            onClick={() =>
                                                treatmentBillingModalToggleHandler(
                                                    ongoingPlansObject[activeTab],
                                                )
                                            }
                                            id={`treatment-billing-${activeTab}`}
                                        >
                                            <svg
                                                className="w-10 h-14"
                                                viewBox="0 0 10 14"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M1.11523 0.277344L2.15625 1.16602L3.17188 0.277344C3.40039 0.0996094 3.73047 0.0996094 3.95898 0.277344L5 1.16602L6.01562 0.277344C6.24414 0.0996094 6.57422 0.0996094 6.80273 0.277344L7.84375 1.16602L8.85938 0.277344C9.03711 0.125 9.29102 0.0996094 9.51953 0.201172C9.72266 0.302734 9.875 0.505859 9.875 0.734375V12.5156C9.875 12.7695 9.72266 12.9727 9.51953 13.0742C9.29102 13.1758 9.03711 13.1504 8.85938 12.998L7.84375 12.1094L6.80273 12.998C6.57422 13.1758 6.24414 13.1758 6.01562 12.998L5 12.1094L3.95898 12.998C3.73047 13.1758 3.40039 13.1758 3.17188 12.998L2.15625 12.1094L1.11523 12.998C0.9375 13.1504 0.683594 13.1758 0.480469 13.0742C0.251953 12.9727 0.125 12.7695 0.125 12.5156V0.734375C0.125 0.505859 0.251953 0.302734 0.480469 0.201172C0.683594 0.0996094 0.9375 0.125 1.11523 0.277344ZM1.34375 11.1953L1.75 10.8398C1.97852 10.6621 2.30859 10.6621 2.53711 10.8398L3.57812 11.7285L4.59375 10.8398C4.82227 10.6621 5.15234 10.6621 5.38086 10.8398L6.42188 11.7285L7.4375 10.8398C7.66602 10.6621 7.99609 10.6621 8.22461 10.8398L8.65625 11.1953V2.08008L8.22461 2.43555C7.99609 2.61328 7.66602 2.61328 7.4375 2.43555L6.42188 1.54688L5.38086 2.43555C5.15234 2.61328 4.82227 2.61328 4.59375 2.43555L3.57812 1.54688L2.53711 2.43555C2.30859 2.61328 1.97852 2.61328 1.75 2.43555L1.34375 2.08008V11.1953ZM3.17188 3.78125H6.82812C7.1582 3.78125 7.4375 4.06055 7.4375 4.39062C7.4375 4.74609 7.1582 5 6.82812 5H3.17188C2.81641 5 2.5625 4.74609 2.5625 4.39062C2.5625 4.06055 2.81641 3.78125 3.17188 3.78125ZM3.17188 8.25H6.82812C7.1582 8.25 7.4375 8.5293 7.4375 8.85938C7.4375 9.21484 7.1582 9.46875 6.82812 9.46875H3.17188C2.81641 9.46875 2.5625 9.21484 2.5625 8.85938C2.5625 8.5293 2.81641 8.25 3.17188 8.25ZM2.5625 6.625C2.5625 6.29492 2.81641 6.01562 3.17188 6.01562H6.82812C7.1582 6.01562 7.4375 6.29492 7.4375 6.625C7.4375 6.98047 7.1582 7.23438 6.82812 7.23438H3.17188C2.81641 7.23438 2.5625 6.98047 2.5625 6.625Z"
                                                    fill="#1A1A1A"
                                                />
                                            </svg>
                                        </button>
                                    </AntTooltip>
                                    <AntTooltip
                                        content="Book Appointment"
                                        placement="bottom"
                                        getPopupContainer={() =>
                                            document.getElementById(
                                                `book-appointment-${activeTab}`,
                                            ) || document.body
                                        }
                                    >
                                        <button
                                            className="p-6"
                                            onClick={onBookAppointment}
                                            id={`book-appointment-${activeTab}`}
                                        >
                                            <svg
                                                className="w-12 h-14"
                                                viewBox="0 0 12 14"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M4.17188 0.734375V1.75H7.82812V0.734375C7.82812 0.404297 8.08203 0.125 8.4375 0.125C8.76758 0.125 9.04688 0.404297 9.04688 0.734375V1.75H10.0625C10.9512 1.75 11.6875 2.48633 11.6875 3.375V3.78125V5V11.5C11.6875 12.4141 10.9512 13.125 10.0625 13.125H1.9375C1.02344 13.125 0.3125 12.4141 0.3125 11.5V5V3.78125V3.375C0.3125 2.48633 1.02344 1.75 1.9375 1.75H2.95312V0.734375C2.95312 0.404297 3.20703 0.125 3.5625 0.125C3.89258 0.125 4.17188 0.404297 4.17188 0.734375ZM1.53125 5V11.5C1.53125 11.7285 1.70898 11.9062 1.9375 11.9062H10.0625C10.2656 11.9062 10.4688 11.7285 10.4688 11.5V5H1.53125Z"
                                                    fill="#1A1A1A"
                                                />
                                            </svg>
                                        </button>
                                    </AntTooltip>
                                    <AntTooltip
                                        content="Preview Work Done"
                                        placement="bottom"
                                        getPopupContainer={() =>
                                            document.getElementById(
                                                `print-work-done-${activeTab}`,
                                            ) || document.body
                                        }
                                    >
                                        <button
                                            className="p-6"
                                            onClick={onPrint}
                                            id={`print-work-done-${activeTab}`}
                                        >
                                            <svg
                                                className="w-12 h-14"
                                                viewBox="0 0 11 13"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M10.5625 11.0234L8.28906 8.77344L10.5391 6.52344C10.7734 6.28906 10.7734 5.9375 10.5391 5.72656L10.0234 5.1875C9.78906 4.97656 9.4375 4.97656 9.22656 5.1875L7 7.4375L5.40625 5.84375C6.50781 5.32812 7.21094 4.08594 6.92969 2.72656C6.64844 1.41406 5.42969 0.5 4.09375 0.5H0.976562C0.578125 0.5 0.25 0.851562 0.25 1.27344V7.46094C0.25 7.76562 0.484375 8.02344 0.789062 8.02344H1.51562C1.82031 8.02344 2.05469 7.76562 2.05469 7.46094V6.14844H3.03906L5.66406 8.77344L3.41406 11.0234C3.20312 11.2344 3.20312 11.5859 3.41406 11.8203L3.95312 12.3594C4.16406 12.5703 4.51562 12.5703 4.72656 12.3594L7 10.0859L9.25 12.3594C9.46094 12.5703 9.8125 12.5703 10.0469 12.3594L10.5625 11.8203C10.7969 11.5859 10.7969 11.2344 10.5625 11.0234ZM2.10156 2.39844H4.14062C4.65625 2.39844 5.05469 2.82031 5.05469 3.33594C5.05469 3.85156 4.63281 4.27344 4.14062 4.27344H2.10156V2.39844Z"
                                                    fill="#1A1A1A"
                                                ></path>
                                            </svg>
                                        </button>
                                    </AntTooltip>
                                    <button
                                        className="px-8 py-4 flex items-center space-x-6 rounded-4 bg-darwin-neutral_new-800"
                                        onClick={() =>
                                            onMarkAsCompleted(ongoingPlansObject[activeTab])
                                        }
                                    >
                                        {isMarkingAsCompleted ? (
                                            <svg
                                                className="w-13 h-13 fill-white animate-spin"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 512 512"
                                            >
                                                <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                            </svg>
                                        ) : (
                                            <svg
                                                className="w-13 h-13 fill-white"
                                                viewBox="0 0 14 14"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M7 1.84375C5.0957 1.84375 3.36914 2.85938 2.4043 4.48438C1.46484 6.13477 1.46484 8.14062 2.4043 9.76562C3.36914 11.416 5.0957 12.4062 7 12.4062C8.87891 12.4062 10.6055 11.416 11.5703 9.76562C12.5098 8.14062 12.5098 6.13477 11.5703 4.48438C10.6055 2.85938 8.87891 1.84375 7 1.84375ZM7 13.625C4.66406 13.625 2.53125 12.4062 1.36328 10.375C0.195312 8.36914 0.195312 5.90625 1.36328 3.875C2.53125 1.86914 4.66406 0.625 7 0.625C9.31055 0.625 11.4434 1.86914 12.6113 3.875C13.7793 5.90625 13.7793 8.36914 12.6113 10.375C11.4434 12.4062 9.31055 13.625 7 13.625ZM9.86914 5.93164L6.61914 9.18164C6.36523 9.43555 5.98438 9.43555 5.75586 9.18164L4.13086 7.55664C3.87695 7.32812 3.87695 6.94727 4.13086 6.71875C4.35938 6.46484 4.74023 6.46484 4.99414 6.71875L6.1875 7.91211L9.00586 5.09375C9.23438 4.83984 9.61523 4.83984 9.86914 5.09375C10.0977 5.32227 10.0977 5.70312 9.86914 5.93164Z" />
                                            </svg>
                                        )}
                                        <p className="text-13 font-700 text-white">
                                            Mark as Completed
                                        </p>
                                    </button>
                                </Fragment>
                            )}
                            {activeTab === TREATMENT_PLAN_TYPE.ESTIMATE && (
                                <button
                                    className="px-8 py-4 flex items-center space-x-6 rounded-4 bg-darwin-neutral_new-800"
                                    onClick={createNewEstimatesModalToggleHandler}
                                >
                                    <svg
                                        className="w-12 h-10 fill-white"
                                        viewBox="0 0 12 12"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M6.8125 1.65625V5.3125H10.4688C10.9004 5.3125 11.2812 5.69336 11.2812 6.125C11.2812 6.58203 10.9004 6.9375 10.4688 6.9375H6.8125V10.5938C6.8125 11.0508 6.43164 11.4062 6 11.4062C5.54297 11.4062 5.1875 11.0508 5.1875 10.5938V6.9375H1.53125C1.07422 6.9375 0.71875 6.58203 0.71875 6.125C0.71875 5.69336 1.07422 5.3125 1.53125 5.3125H5.1875V1.65625C5.1875 1.22461 5.54297 0.84375 6 0.84375C6.43164 0.84375 6.8125 1.22461 6.8125 1.65625Z" />
                                    </svg>
                                    <p className="text-13 font-700 text-white">Add new</p>
                                </button>
                            )}
                        </div>
                    </div>

                    {activeTab === TREATMENT_PLAN_TYPE.ESTIMATE ? (
                        estimatesFetchState === 'loading' ? (
                            <div
                                className="flex-1 overflow-auto flex items-center justify-center bg-white rounded-8"
                                style={{
                                    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                                }}
                            >
                                <svg
                                    className="w-24 h-24 fill-darwin-accent-bright-blue-800 animate-spin"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                >
                                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                </svg>
                            </div>
                        ) : estimatesFetchState === 'error' ? (
                            <div
                                className="flex-1 overflow-auto flex flex-col items-center justify-center bg-white rounded-8 space-y-12"
                                style={{
                                    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                                }}
                            >
                                <div className="flex items-center justify-center w-80 h-80">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 576 512"
                                        className="w-80 h-80 fill-darwin-accent-bright-blue-800 opacity-70"
                                    >
                                        <path d="M248.747 204.705l6.588 112c.373 6.343 5.626 11.295 11.979 11.295h41.37a12 12 0 0 0 11.979-11.295l6.588-112c.405-6.893-5.075-12.705-11.979-12.705h-54.547c-6.903 0-12.383 5.812-11.978 12.705zM330 384c0 23.196-18.804 42-42 42s-42-18.804-42-42 18.804-42 42-42 42 18.804 42 42zm-.423-360.015c-18.433-31.951-64.687-32.009-83.154 0L6.477 440.013C-11.945 471.946 11.118 512 48.054 512H527.94c36.865 0 60.035-39.993 41.577-71.987L329.577 23.985zM53.191 455.002L282.803 57.008c2.309-4.002 8.085-4.002 10.394 0l229.612 397.993c2.308 4-.579 8.998-5.197 8.998H58.388c-4.617.001-7.504-4.997-5.197-8.997z" />
                                    </svg>
                                </div>
                                <div className="space-y-4">
                                    <p className="text-darwin-neutrals-1000 font-700 leading-5 text-center">
                                        Failed to load estimates
                                    </p>
                                    <p className="text-darwin-neutrals-600 text-13 leading-5 text-center">
                                        <button
                                            onClick={() => fetchEstimates(patientId)}
                                            className="font-700 underline text-darwin-accent-bright-blue-800"
                                        >
                                            Refresh
                                        </button>{' '}
                                        the page to try again.
                                    </p>
                                </div>
                            </div>
                        ) : Array.isArray(estimates) && estimates.length > 0 && docProfile ? (
                            <EstimateAndCompletedPlan
                                activeTabPlans={estimates}
                                visibleItems={visibleItems}
                                onVisibleItemsChange={onVisibleItemsChange}
                                activeTab={activeTab}
                                onActivate={onActivate}
                                onEdit={onEdit}
                                treatmentBillingModalToggleHandler={
                                    treatmentBillingModalToggleHandler
                                }
                                ptDetails={ptDetails}
                                docProfile={docProfile}
                            />
                        ) : (
                            <div
                                className="p-20 flex-1 overflow-auto flex flex-col items-center justify-center space-y-18 bg-white rounded-8"
                                style={{
                                    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                                }}
                            >
                                <img
                                    className="w-116 h-92"
                                    src="/images/plan-estimates.png"
                                    alt="plan estimates"
                                />
                                <p className="text-13 text-darwin-neutrals-1000">
                                    No estimates added
                                </p>
                                <button
                                    className="px-8 py-4 flex items-center space-x-6 rounded-4 bg-darwin-accent-bright-blue-800"
                                    onClick={createNewEstimatesModalToggleHandler}
                                >
                                    <svg
                                        className="w-12 h-10 fill-white"
                                        viewBox="0 0 12 12"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M6.8125 1.65625V5.3125H10.4688C10.9004 5.3125 11.2812 5.69336 11.2812 6.125C11.2812 6.58203 10.9004 6.9375 10.4688 6.9375H6.8125V10.5938C6.8125 11.0508 6.43164 11.4062 6 11.4062C5.54297 11.4062 5.1875 11.0508 5.1875 10.5938V6.9375H1.53125C1.07422 6.9375 0.71875 6.58203 0.71875 6.125C0.71875 5.69336 1.07422 5.3125 1.53125 5.3125H5.1875V1.65625C5.1875 1.22461 5.54297 0.84375 6 0.84375C6.43164 0.84375 6.8125 1.22461 6.8125 1.65625Z" />
                                    </svg>
                                    <p className="text-13 font-700 text-white">Add new</p>
                                </button>
                            </div>
                        )
                    ) : activeTab === TREATMENT_PLAN_TYPE.COMPLETED_PLAN ? (
                        completedPlansFetchState === 'loading' ? (
                            <div
                                className="flex-1 overflow-auto flex items-center justify-center bg-white rounded-8"
                                style={{
                                    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                                }}
                            >
                                <svg
                                    className="w-24 h-24 fill-darwin-accent-bright-blue-800 animate-spin"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                >
                                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                </svg>
                            </div>
                        ) : completedPlansFetchState === 'error' ? (
                            <div
                                className="flex-1 overflow-auto flex flex-col items-center justify-center bg-white rounded-8 space-y-12"
                                style={{
                                    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                                }}
                            >
                                <div className="flex items-center justify-center w-80 h-80">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 576 512"
                                        className="w-80 h-80 fill-darwin-accent-bright-blue-800 opacity-70"
                                    >
                                        <path d="M248.747 204.705l6.588 112c.373 6.343 5.626 11.295 11.979 11.295h41.37a12 12 0 0 0 11.979-11.295l6.588-112c.405-6.893-5.075-12.705-11.979-12.705h-54.547c-6.903 0-12.383 5.812-11.978 12.705zM330 384c0 23.196-18.804 42-42 42s-42-18.804-42-42 18.804-42 42-42 42 18.804 42 42zm-.423-360.015c-18.433-31.951-64.687-32.009-83.154 0L6.477 440.013C-11.945 471.946 11.118 512 48.054 512H527.94c36.865 0 60.035-39.993 41.577-71.987L329.577 23.985zM53.191 455.002L282.803 57.008c2.309-4.002 8.085-4.002 10.394 0l229.612 397.993c2.308 4-.579 8.998-5.197 8.998H58.388c-4.617.001-7.504-4.997-5.197-8.997z" />
                                    </svg>
                                </div>
                                <div className="space-y-4">
                                    <p className="text-darwin-neutrals-1000 font-700 leading-5 text-center">
                                        Failed to load completed plans
                                    </p>
                                    <p className="text-darwin-neutrals-600 text-13 leading-5 text-center">
                                        <button
                                            onClick={() => this.fetchCompletedPlans(patientId)}
                                            className="font-700 underline text-darwin-accent-bright-blue-800"
                                        >
                                            Refresh
                                        </button>{' '}
                                        the page to try again.
                                    </p>
                                </div>
                            </div>
                        ) : Array.isArray(completedPlans) &&
                          completedPlans.length > 0 &&
                          docProfile ? (
                            <EstimateAndCompletedPlan
                                activeTabPlans={completedPlans}
                                visibleItems={visibleItems}
                                onVisibleItemsChange={onVisibleItemsChange}
                                activeTab={activeTab}
                                onActivate={onActivate}
                                onEdit={onEdit}
                                treatmentBillingModalToggleHandler={
                                    treatmentBillingModalToggleHandler
                                }
                                ptDetails={ptDetails}
                                docProfile={docProfile}
                            />
                        ) : (
                            <div
                                className="p-20 flex-1 overflow-auto flex flex-col items-center justify-center space-y-18 bg-white rounded-8"
                                style={{
                                    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                                }}
                            >
                                <img
                                    className="w-116 h-92"
                                    src="/images/plan-estimates.png"
                                    alt="plan estimates"
                                />
                                <p className="text-13 text-darwin-neutrals-1000">
                                    No completed plans added
                                </p>
                            </div>
                        )
                    ) : !VALID_ACTIVE_TAB_SET.has(activeTab) ? (
                        <OngoingPlan treatmentPlan={ongoingPlansObject[activeTab]} />
                    ) : (
                        <div
                            className="p-20 flex-1 overflow-auto flex flex-col items-center justify-center space-y-18 bg-white rounded-8"
                            style={{
                                boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                            }}
                        >
                            <img
                                className="w-116 h-92"
                                src="/images/plan-estimates.png"
                                alt="plan estimates"
                            />
                            <button
                                className="px-8 py-4 flex items-center space-x-6 rounded-4 bg-darwin-accent-bright-blue-800"
                                onClick={createNewEstimatesModalToggleHandler}
                            >
                                <svg
                                    className="w-12 h-10 fill-white"
                                    viewBox="0 0 12 12"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M6.8125 1.65625V5.3125H10.4688C10.9004 5.3125 11.2812 5.69336 11.2812 6.125C11.2812 6.58203 10.9004 6.9375 10.4688 6.9375H6.8125V10.5938C6.8125 11.0508 6.43164 11.4062 6 11.4062C5.54297 11.4062 5.1875 11.0508 5.1875 10.5938V6.9375H1.53125C1.07422 6.9375 0.71875 6.58203 0.71875 6.125C0.71875 5.69336 1.07422 5.3125 1.53125 5.3125H5.1875V1.65625C5.1875 1.22461 5.54297 0.84375 6 0.84375C6.43164 0.84375 6.8125 1.22461 6.8125 1.65625Z" />
                                </svg>
                                <p className="text-13 font-700 text-white">Add new estimate</p>
                            </button>
                        </div>
                    )}
                </div>

                <BottomActionStrip onContinue={onContinue} />

                <AnimatePresence>
                    {isCreateNewEstimatesModalVisible && (
                        <CreateNewEstimates
                            closeMe={createNewEstimatesModalToggleHandler}
                            editEstimate={editEstimate}
                            fetchEstimates={fetchEstimates}
                            ptDetails={ptDetails}
                            patientId={patientId}
                            clinicId={prescription?.clinic._id || ''}
                        />
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {treatmentBilling && (
                        <TreatmentBilling
                            treatmentBilling={treatmentBilling}
                            closeMe={() => treatmentBillingModalToggleHandler()}
                            ptDetails={ptDetails}
                            billingPopupData={billingPopupData}
                            isForWorkDone={isForWorkDone}
                            aId={prescription?.visitId}
                            dentalProcedures={dentalProcedures}
                        />
                    )}
                </AnimatePresence>
            </div>
        );
    }
}

export default connect((state: GlobalState) => ({
    prescription: getPrescription(state),
    ongoingTreatmentPlans: getOngoingTreatmentPlans(state),
    dentalProcedures: getDentalProcedures(state),
    docProfile: getRxDoctor(state),
}))(withRouter(PPTreatmentPlan));
